import 'mapbox-gl/dist/mapbox-gl.css';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Source } from 'react-map-gl/mapbox';
import ReactModal from 'react-modal';
import { Tooltip } from 'react-tooltip';
import { GlobalContext } from 'src/global-context';
import DistrictHoverInfo from 'src/custom-components/DistrictHoverInfo';
import DistrictContextMenu from 'src/custom-components/DistrictContextMenu';
import InstitutionTopCard from 'src/custom-components/InstitutionTopCard';
import CustomList from 'src/custom-components/CustomList';
import LoadingBars from 'src/custom-components/LoadingBars';
import General from 'src/utils/GeneralUtils';
import { MAPBOX_ACCESS_TOKEN } from 'src/utils/Constants';
import { Entity } from 'src/utils/CustomTypes';
import { BeaconTool, useNavigatorStore } from 'src/zustand/useNavigatorStore';
import { DEFAULT_BEACON_FILL } from 'src/components/navigator/beacon-colors';
import type { Edulead } from 'edulead';
import { TokenPagination } from 'src/components/TokenPagination';
import { useTokenPager } from 'src/services/edulead-v4';
import { eduleadV4 } from 'src/services/edulead-v4-client';
import { legacyInstitutionsResponse } from 'src/utils/legacyInstitution';
import { legacyPeopleResponse } from 'src/utils/legacyPerson';
import { Bounds, DistrictExtent, extentOf, growBounds, radiusCircles } from 'src/utils/radiusCircles';
import type { BeaconIdsVerb } from 'src/components/navigator/beacon-ids';

/**
 * Hover info for tooltip display
 */
interface HoverInfo {
  feature: any;
  x: number;
  y: number;
}

// Map configuration constants
const MAP_STYLE = 'mapbox://styles/mapbox/dark-v11';
const TILESET_URL = 'mapbox://jiril.8jwitz2j';
const SOURCE_LAYER = 'district-boundaries-geojson-m-7dyckv';
// The fill of a highlighted beacon's districts without a color of its own, under hover and
// click: the palette's --amplify-colors-orange-50 (the same in both themes; Mapbox paint cannot
// read CSS variables). A beacon with a color fills in it (beacon-colors.ts).
const BEACON_FILL_COLOR = DEFAULT_BEACON_FILL;
const BEACON_FILL_OPACITY = 1;
// The fill of the districts the query counted people at: the palette's
// --amplify-colors-purple-60 (light theme), more opaque for more people, from 1 person, then 5,
// 20, 50 and 100 or more.
const HEADCOUNT_FILL_COLOR = '#6a6dfc';
const HEADCOUNT_OPACITY_STEPS: [number, number][] = [[1, 0.4], [5, 0.55], [20, 0.7], [50, 0.85], [100, 1]];
// The white tint over the district under the mouse and the clicked one.
const HOVER_FILL_OPACITY = 0.25;
const CLICKED_FILL_OPACITY = 0.5;
// Names in three tiers as the map (zoom 4 to 10) zooms in: states, then cities, then districts.
// A layer shows at zoom >= its min and < its max. Mapbox drops the district labels that would
// overlap, so fewer show at the low end of their band and nearly all at the high end. The base
// style's own labels never show: the custom background and water fill below sit on top of them.
const STATE_LABEL_MAX_ZOOM = 6;
const CITY_LABEL_MIN_ZOOM = 6;
const CITY_LABEL_MAX_ZOOM = 7;
const DISTRICT_LABEL_MIN_ZOOM = 7;
// The muted grey of the state and city labels, and white for the highlighted beacons' districts.
const LABEL_COLOR = '#bebccc';
const LABEL_HIGHLIGHT_COLOR = '#ffffff';
const LABEL_HALO_COLOR = '#1f1f29';
// A rectangle or oval drag shorter than this on both sides is a click, not a marquee.
const MARQUEE_MIN_PX = 4;
// Where the oval is probed for a district larger than it (one with no drawn vertex inside it):
// its centre and the midpoints of its half-axes, as fractions of the half-axes.
const OVAL_PROBES: [number, number][] = [[0, 0], [0.5, 0], [-0.5, 0], [0, 0.5], [0, -0.5]];

/** A rectangle or oval being dragged: its corners in map-container pixels and the stroke's verb. */
interface Marquee {
  shape: 'rectangle' | 'oval';
  verb: BeaconIdsVerb;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Whether a container point lies in the ellipse that fills the marquee's box. */
const inOval = (m: Marquee, x: number, y: number) => {
  const a = Math.abs(m.x1 - m.x0) / 2;
  const b = Math.abs(m.y1 - m.y0) / 2;
  if (a === 0 || b === 0) return false;
  const dx = (x - (m.x0 + m.x1) / 2) / a;
  const dy = (y - (m.y0 + m.y1) / 2) / b;
  return dx * dx + dy * dy <= 1;
};

export function MapboxMap() {
  // Read data from Zustand store
  const { highlightedInstitutionIds, highlightFills, beaconFill, highlightedRadiusMiles, headcounts, headcountsConditions, editingBeaconId, paintBeacon, beaconTool } = useNavigatorStore();
  // The fill of the beacon in hand, for the handlers' closures.
  const beaconFillRef = useRef(beaconFill);
  useEffect(() => {
    beaconFillRef.current = beaconFill;
  }, [beaconFill]);
  // The people conditions the counts on the map are of: the Navigator panel's query's.
  const headcountsPeopleConditions = headcountsConditions;
  const headcountsKey = JSON.stringify(headcountsPeopleConditions);
  const isEditingBeacon = editingBeaconId !== null;

  const globalContext = useContext(GlobalContext);
  const mapRef = useRef<any>(null);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);
  // The box the Navigator query's people are in, fitted once per query (not again on coming
  // back to the Map view); the panel on the right and the switch on the top left are kept clear.
  const headcountsFocus = useNavigatorStore((state) => state.headcountsFocus);
  const headcountsQueryKey = useNavigatorStore((state) => state.headcountsQueryKey);
  const focusedQueryKey = useNavigatorStore((state) => state.focusedQueryKey);
  const setFocusedQueryKey = useNavigatorStore((state) => state.setFocusedQueryKey);
  const mapViewState = useNavigatorStore((state) => state.mapViewState);
  const setMapViewState = useNavigatorStore((state) => state.setMapViewState);
  useEffect(() => {
    if (!isStyleLoaded || !headcountsFocus || !headcountsQueryKey) return;
    if (focusedQueryKey === headcountsQueryKey) return;
    mapRef.current?.fitBounds(headcountsFocus, {
      padding: { top: 70, right: 390, bottom: 40, left: 40 },
      maxZoom: 9,
      duration: 1200,
    });
    setFocusedQueryKey(headcountsQueryKey);
  }, [isStyleLoaded, headcountsFocus, headcountsQueryKey, focusedQueryKey, setFocusedQueryKey]);
  const [tilesLoadedTrigger, setTilesLoadedTrigger] = useState(0);
  const [hoverInfo, setHoverInfo] = useState<HoverInfo | null>(null);
  const hoveredFeatureRef = useRef<{ id: string; source: string; sourceLayer: string } | null>(null);
  const clickedFeatureRef = useRef<{ id: string; source: string; sourceLayer: string } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Slideout state
  const [selectedDistrict, setSelectedDistrict] = useState<any>(null);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'schools' | 'people'>('schools');

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; district: any; shape: { id: string; name: string }[] } | null>(null);

  // Cache for GEOID → institution IDs mapping (persists across renders)
  // Built incrementally as we see features from querySourceFeatures
  const geoidToInstitutionsCache = useRef<Map<string, string[]>>(new window.Map());

  // The GEOIDs whose `beacon` feature state is set, to clear when the highlight changes
  const beaconGeoids = useRef<Set<string>>(new window.Set());
  // The GEOIDs whose `headcount` feature state is set, to clear when the search changes
  const headcountGeoids = useRef<Set<string>>(new window.Set());
  // Each highlighted district's box, grown as more of its tiles load
  const beaconBounds = useRef<Map<string, Bounds>>(new window.Map());
  const [beaconExtents, setBeaconExtents] = useState<DistrictExtent[]>([]);

  // Editing a beacon: the open beacon's members, as a set the handlers can read without a stale
  // closure; and the stroke under way, if any.
  const beaconMemberSet = useRef<Set<string>>(new window.Set());
  const strokeRef = useRef<{ verb: BeaconIdsVerb; geoids: Set<string>; ids: Set<string>; names: Record<string, string> } | null>(null);
  // Set when a brush stroke or marquee ends, so the click Mapbox fires right after does not toggle
  // the first district a second time.
  const strokeJustEndedRef = useRef(false);
  const editingBeaconIdRef = useRef<string | null>(null);
  // The selection tool, for the handlers; and the rectangle or oval being dragged, in a ref for
  // the handlers and in state for the overlay that draws it.
  const beaconToolRef = useRef<BeaconTool>('pointer');
  // Space held with a tool picked pans, as in Photoshop: the tool claims no drag meanwhile.
  const spaceDownRef = useRef(false);
  const [isSpaceDown, setIsSpaceDown] = useState(false);
  const marqueeRef = useRef<Marquee | null>(null);
  const marqueeFrameRef = useRef<number | null>(null);
  const [marquee, setMarquee] = useState<Marquee | null>(null);
  useEffect(() => {
    beaconToolRef.current = beaconTool;
  }, [beaconTool]);

  // Space is watched only while a beacon is open, and not while typing (the beacon's name has
  // spaces); leaving the window with it held drops it.
  useEffect(() => {
    if (!isEditingBeacon) {
      spaceDownRef.current = false;
      setIsSpaceDown(false);
      return;
    }
    const setSpace = (down: boolean) => {
      spaceDownRef.current = down;
      setIsSpaceDown(down);
    };
    const isTyping = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || isTyping(e.target)) return;
      // Space on the page would scroll it, and repeat while held.
      e.preventDefault();
      if (!e.repeat) setSpace(true);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setSpace(false);
    };
    const onBlur = () => setSpace(false);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [isEditingBeacon]);
  useEffect(() => {
    editingBeaconIdRef.current = editingBeaconId;
    // Closing the beacon mid-stroke drops the stroke; the store has nothing to paint into.
    if (!editingBeaconId) {
      strokeRef.current = null;
      marqueeRef.current = null;
      setMarquee(null);
    }
  }, [editingBeaconId]);

  const highlightedSet = useMemo(() => new window.Set(highlightedInstitutionIds), [highlightedInstitutionIds]);
  useEffect(() => {
    beaconMemberSet.current = highlightedSet;
  }, [highlightedSet]);

  /** A shape's districts (a GEOID can hold several; null ids are placeholders), id and name. */
  const shapeDistricts = (feature: any): { id: string; name: string }[] => {
    if (!feature?.properties?.districts) return [];
    try {
      const districts = JSON.parse(feature.properties.districts) as any[];
      return districts
        .filter((d) => typeof d?.id === 'string')
        .map((d) => ({ id: d.id as string, name: typeof d.name === 'string' ? d.name : '' }));
    } catch {
      return [];
    }
  };
  const shapeDistrictIds = (feature: any): string[] => shapeDistricts(feature).map((d) => d.id);

  /** Whether any of the shape's districts is in the open beacon: then the shape is lit. */
  const isShapeInBeacon = (feature: any) =>
    shapeDistrictIds(feature).some((id) => beaconMemberSet.current.has(id));

  /**
   * Adds the shape under the pointer to the stroke and lights it (or puts it out) at once; the
   * store and the beacon follow when the stroke ends.
   */
  const strokeOver = (map: any, feature: any) => {
    const stroke = strokeRef.current;
    const geoid = feature?.properties?.GEOID;
    if (!stroke || !geoid || stroke.geoids.has(geoid)) return;
    const districts = shapeDistricts(feature);
    if (districts.length === 0) return;
    stroke.geoids.add(geoid);
    districts.forEach((d) => {
      stroke.ids.add(d.id);
      if (d.name) stroke.names[d.id] = d.name;
    });
    const lit = stroke.verb === 'Added';
    map.setFeatureState({ source: 'districts', sourceLayer: SOURCE_LAYER, id: geoid }, { beacon: lit, beaconColor: beaconFillRef.current });
    // The highlight effect un-lights only what it knows is lit: tell it.
    if (lit) beaconGeoids.current.add(geoid);
    else beaconGeoids.current.delete(geoid);
  };

  /** Ends the stroke: the districts it crossed go to the edited set. */
  const endStroke = () => {
    const stroke = strokeRef.current;
    if (!stroke) return;
    strokeRef.current = null;
    if (stroke.ids.size > 0) paintBeacon([...stroke.ids], stroke.verb, stroke.names);
  };

  /**
   * What a gesture does: Shift forces an add and Alt (Option) a removal, as in the drawing tools;
   * otherwise the first district under the pointer decides: out → add, in → remove, none → add.
   */
  const verbFor = (first: any, originalEvent: MouseEvent | undefined): BeaconIdsVerb => {
    if (originalEvent?.altKey) return 'Removed';
    if (originalEvent?.shiftKey) return 'Added';
    return first && isShapeInBeacon(first) ? 'Removed' : 'Added';
  };

  /** Starts a brush stroke at the feature under the pointer. */
  const startStroke = (map: any, first: any, verb: BeaconIdsVerb) => {
    strokeRef.current = { verb, geoids: new window.Set(), ids: new window.Set(), names: {} };
    if (first) strokeOver(map, first);
  };

  /**
   * The districts a finished marquee covers: for a rectangle, what Mapbox finds in the box; for an
   * oval, those of them with a drawn vertex inside the ellipse, plus whatever lies under a few
   * probe points inside it (a district larger than the oval has no vertex in it).
   *
   * The vertex test projects with arithmetic, not map.project(): a box of districts holds some
   * hundred thousand vertices, and Mercator is affine to the screen while the map is not rotated
   * or tilted (the one case where map.project() is used).
   */
  const marqueeDistricts = (map: any, m: Marquee) => {
    const minX = Math.min(m.x0, m.x1);
    const maxX = Math.max(m.x0, m.x1);
    const minY = Math.min(m.y0, m.y1);
    const maxY = Math.max(m.y0, m.y1);
    const layers = { layers: ['district-fill'] };
    const picked = new window.Map<string, { id: string; name: string }[]>();
    const take = (feature: any) => {
      const geoid = feature?.properties?.GEOID;
      if (geoid && !picked.has(geoid)) picked.set(geoid, shapeDistricts(feature));
    };
    const inBox = map.queryRenderedFeatures([[minX, minY], [maxX, maxY]], layers);
    if (m.shape === 'rectangle') {
      inBox.forEach(take);
      return picked;
    }

    // Screen x is linear in longitude and screen y in the Mercator y of latitude: fit both from
    // the box's corners.
    const flat = map.getBearing() === 0 && map.getPitch() === 0;
    const nw = map.unproject([minX, minY]);
    const se = map.unproject([maxX, maxY]);
    const mercY = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
    const xPerLng = (maxX - minX) / (se.lng - nw.lng);
    const yPerMerc = (maxY - minY) / (mercY(se.lat) - mercY(nw.lat));
    const toScreen = (lng: number, lat: number): [number, number] => {
      if (!flat) {
        const p = map.project([lng, lat]);
        return [p.x, p.y];
      }
      return [minX + (lng - nw.lng) * xPerLng, minY + (mercY(lat) - mercY(nw.lat)) * yPerMerc];
    };
    inBox.forEach((feature: any) => {
      const rings: number[][][] =
        feature.geometry?.type === 'Polygon'
          ? feature.geometry.coordinates
          : feature.geometry?.type === 'MultiPolygon'
            ? feature.geometry.coordinates.flat()
            : [];
      const touches = rings.some((ring) =>
        ring.some(([lng, lat]) => {
          const [x, y] = toScreen(lng, lat);
          return inOval(m, x, y);
        }),
      );
      if (touches) take(feature);
    });
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    OVAL_PROBES.forEach(([fx, fy]) => {
      const x = cx + (fx * (maxX - minX)) / 2;
      const y = cy + (fy * (maxY - minY)) / 2;
      map.queryRenderedFeatures([x, y], layers).forEach(take);
    });
    return picked;
  };

  /** Ends the marquee: the districts it covers go to the edited set with its verb. */
  const endMarquee = (map: any) => {
    const m = marqueeRef.current;
    if (!m) return false;
    marqueeRef.current = null;
    setMarquee(null);
    if (Math.abs(m.x1 - m.x0) < MARQUEE_MIN_PX && Math.abs(m.y1 - m.y0) < MARQUEE_MIN_PX) return false;
    const ids = new window.Set<string>();
    const names: Record<string, string> = {};
    marqueeDistricts(map, m).forEach((districts) =>
      districts.forEach((d) => {
        ids.add(d.id);
        if (d.name) names[d.id] = d.name;
      }),
    );
    if (ids.size > 0) paintBeacon([...ids], m.verb, names);
    return true;
  };

  /**
   * Adds the loaded tiles' districts to the GEOID → institution IDs cache. Tiles load as the map
   * moves, so the cache grows over time.
   */
  // querySourceFeatures reads every loaded feature's geometry out of the tiles, which is slow for
  // a viewport of districts; the cache is rebuilt once per tile load, not once per effect run.
  const tilesTriggerRef = useRef(0);
  tilesTriggerRef.current = tilesLoadedTrigger;
  const cachedTriggerRef = useRef(-1);
  const cacheLoadedDistricts = useCallback((map: any) => {
    if (cachedTriggerRef.current === tilesTriggerRef.current) return;
    cachedTriggerRef.current = tilesTriggerRef.current;
    const features = map.querySourceFeatures('districts', { sourceLayer: SOURCE_LAYER });
    features.forEach((feature: any) => {
      const geoid = feature.properties.GEOID;
      if (!geoid || geoidToInstitutionsCache.current.has(geoid)) return;
      const districtsJson = feature.properties.districts;
      if (!districtsJson) return;
      try {
        const districts = JSON.parse(districtsJson) as any[];
        if (districts && districts.length > 0) {
          geoidToInstitutionsCache.current.set(geoid, districts.map((d: any) => d.id).filter(Boolean));
        }
      } catch (e) {
        console.error('[MapboxMap] Error parsing districts JSON for GEOID', geoid, e);
      }
    });
  }, []);

  // The selected district's schools, most students first, while the Schools tab is open; a new
  // district or tab starts again from page 1.
  const schoolsPager = useTokenPager(
    (pageToken) => selectedDistrict?.id && activeTab === 'schools'
      ? eduleadV4.institutions.search({
          conditions: JSON.stringify([
            { field: 'type', operator: 'equals', value: 'SCHOOL' },
            { field: 'idParentInstitution', operator: 'equals', value: selectedDistrict.id },
          ]),
          order_by: 'student_count desc',
          page_size: 25,
          page_token: pageToken,
        })
      : Promise.resolve({ data: [], response: {} as Edulead.EduleadV4SearchInstitutionsResponse }),
    [selectedDistrict?.id, activeTab],
    'Error loading schools'
  );
  const schoolsSearchResponse = schoolsPager.items && legacyInstitutionsResponse(schoolsPager.items, schoolsPager.response?.total_size);
  const isLoadingSchools = schoolsPager.loading;

  // The selected district's people, highest priority first, while the People tab is open; a new
  // district or tab starts again from page 1. With a search's counts on the map, only its people
  // there: the ones its count for the district is of.
  const contactsPager = useTokenPager(
    (pageToken) => selectedDistrict?.id && activeTab === 'people'
      ? eduleadV4.people.search({
          conditions: JSON.stringify([
            { field: 'institutionId', operator: 'equals', value: selectedDistrict.id },
            ...(headcountsPeopleConditions ?? []),
          ]),
          source: 'MAP_VIEW',
          order_by: 'priority desc',
          page_size: 25,
          page_token: pageToken,
        })
      : Promise.resolve({ data: [], response: {} as Edulead.EduleadV4SearchPeopleResponse }),
    [selectedDistrict?.id, activeTab, headcountsKey],
    'Error loading contacts'
  );
  const contactsSearchResponse = contactsPager.items && contactsPager.response && legacyPeopleResponse(contactsPager.items, contactsPager.response);
  const isLoadingContacts = contactsPager.loading;

  // A new district opens on its schools; with a search's counts on the map, on the people it
  // counted there, which is what the district was clicked for.
  useEffect(() => {
    if (selectedDistrict?.id) {
      setActiveTab(headcountsPeopleConditions ? 'people' : 'schools');
    }
  }, [selectedDistrict?.id]);

  // Throttle hover handler to reduce re-renders
  const lastHoverTime = useRef<number>(0);
  const HOVER_THROTTLE_MS = 16; // ~60fps

  const onHover = useCallback((event: any) => {
    const {
      features,
      point: { x, y }
    } = event;

    const hoveredFeature = features && features[0];
    const map = mapRef.current;

    if (!map) return;

    // A marquee follows the pointer, drawn once a frame; the hover highlight rests meanwhile, as
    // every re-render of this component is dear.
    if (marqueeRef.current) {
      marqueeRef.current = { ...marqueeRef.current, x1: x, y1: y };
      if (marqueeFrameRef.current === null) {
        marqueeFrameRef.current = requestAnimationFrame(() => {
          marqueeFrameRef.current = null;
          if (marqueeRef.current) setMarquee(marqueeRef.current);
        });
      }
      return;
    }

    // A stroke takes every district the pointer crosses, so it is not throttled.
    if (strokeRef.current) {
      if (hoveredFeature) strokeOver(map, hoveredFeature);
    } else {
      // Throttle hover events
      const now = Date.now();
      if (now - lastHoverTime.current < HOVER_THROTTLE_MS) {
        return;
      }
      lastHoverTime.current = now;
    }

    if (hoveredFeature) {
      // Remove hover from previous feature
      if (hoveredFeatureRef.current) {
        map.setFeatureState(
          {
            source: hoveredFeatureRef.current.source,
            sourceLayer: hoveredFeatureRef.current.sourceLayer,
            id: hoveredFeatureRef.current.id
          },
          { hover: false }
        );
      }

      // Add hover to new feature
      const featureId = hoveredFeature.properties.GEOID;
      if (featureId) {
        map.setFeatureState(
          {
            source: 'districts',
            sourceLayer: SOURCE_LAYER,
            id: featureId
          },
          { hover: true }
        );

        hoveredFeatureRef.current = {
          id: featureId,
          source: 'districts',
          sourceLayer: SOURCE_LAYER
        };
      }

      setHoverInfo({ feature: hoveredFeature, x, y });
    } else {
      // Remove hover from previous feature
      if (hoveredFeatureRef.current) {
        map.setFeatureState(
          {
            source: hoveredFeatureRef.current.source,
            sourceLayer: hoveredFeatureRef.current.sourceLayer,
            id: hoveredFeatureRef.current.id
          },
          { hover: false }
        );
        hoveredFeatureRef.current = null;
      }
      setHoverInfo(null);
    }
  }, []);

  const onClick = useCallback((event: any) => {
    const { features } = event;
    const clickedFeature = features && features[0];
    const map = mapRef.current;

    if (!map) return;

    // With a beacon open, a click adds the district if it is out and removes it if it is in: a
    // stroke of one district. The click after a brush stroke or marquee is that gesture's own.
    if (editingBeaconIdRef.current) {
      if (strokeJustEndedRef.current) {
        strokeJustEndedRef.current = false;
        return;
      }
      // Nothing moves while the beacon's edits are being saved.
      if (!clickedFeature || useNavigatorStore.getState().isSavingBeacon) return;
      startStroke(map.getMap(), clickedFeature, verbFor(clickedFeature, event.originalEvent));
      endStroke();
      return;
    }

    if (clickedFeature) {
      const allDistricts = clickedFeature.properties.districts ? JSON.parse(clickedFeature.properties.districts) : [];
      const districts = allDistricts.filter((d: any) => d.id !== null);

      // Don't open slideout if no valid districts
      if (districts.length === 0) {
        return;
      }

      const district = districts[0] || {};

      // Remove click from previous feature
      if (clickedFeatureRef.current) {
        map.setFeatureState(
          {
            source: clickedFeatureRef.current.source,
            sourceLayer: clickedFeatureRef.current.sourceLayer,
            id: clickedFeatureRef.current.id
          },
          { clicked: false }
        );
      }

      // Add click to new feature
      const featureId = clickedFeature.properties.GEOID;
      if (featureId) {
        map.setFeatureState(
          {
            source: 'districts',
            sourceLayer: SOURCE_LAYER,
            id: featureId
          },
          { clicked: true }
        );

        clickedFeatureRef.current = {
          id: featureId,
          source: 'districts',
          sourceLayer: SOURCE_LAYER
        };
      }

      // Hide hover info and set clicked district
      setHoverInfo(null);
      setSelectedDistrict(district);
      setIsSlideoutOpen(true);
    }
  }, []);

  /**
   * With a beacon open, a drag that the tool claims (the brush's, a rectangle's or oval's) starts
   * here: preventDefault() keeps Mapbox from panning or box-zooming on this gesture; verbFor()
   * says whether it adds or removes. The pointer tool claims nothing: its drag pans.
   */
  const onMouseDown = useCallback((event: any) => {
    if (!editingBeaconIdRef.current || event.originalEvent?.button !== 0 || spaceDownRef.current) return;
    const tool = beaconToolRef.current;
    const brushes = tool === 'brush';
    const draws = tool === 'rectangle' || tool === 'oval';
    if (!brushes && !draws) return;
    event.preventDefault();
    if (useNavigatorStore.getState().isSavingBeacon) return;
    const map = mapRef.current?.getMap();
    if (!map) return;
    const first = event.features && event.features[0];
    const verb = verbFor(first, event.originalEvent);
    if (brushes) {
      startStroke(map, first, verb);
    } else {
      const { x, y } = event.point;
      marqueeRef.current = {
        shape: tool as 'rectangle' | 'oval',
        verb,
        x0: x,
        y0: y,
        x1: x,
        y1: y,
      };
      setMarquee(marqueeRef.current);
    }
    setHoverInfo(null);
  }, []);

  /** Ends the stroke or marquee under way; a marquee too small to be a drag is left to the click. */
  const finishGesture = () => {
    const map = mapRef.current?.getMap();
    let ended = false;
    if (strokeRef.current) {
      endStroke();
      ended = true;
    } else if (marqueeRef.current && map) {
      ended = endMarquee(map);
    }
    if (!ended) return;
    // Mapbox fires the click, if any, right after the mouseup; a dragged gesture fires none, so
    // the flag is dropped on the next tick rather than waiting for a click that never comes.
    strokeJustEndedRef.current = true;
    setTimeout(() => {
      strokeJustEndedRef.current = false;
    }, 0);
  };

  const onMouseUp = useCallback(() => finishGesture(), []);

  // A marquee released outside the map still ends where the pointer last was over it.
  useEffect(() => {
    if (!marquee) return;
    const onWindowUp = () => finishGesture();
    window.addEventListener('mouseup', onWindowUp);
    return () => window.removeEventListener('mouseup', onWindowUp);
  }, [marquee !== null]);

  const onContextMenu = useCallback((event: any) => {
    event.preventDefault();

    const { features, point } = event;
    const clickedFeature = features && features[0];

    if (clickedFeature) {
      const allDistricts = clickedFeature.properties.districts ? JSON.parse(clickedFeature.properties.districts) : [];
      const districts = allDistricts.filter((d: any) => d.id !== null);

      if (districts.length > 0) {
        const district = districts[0] || {};

        // Hide hover info when context menu opens
        setHoverInfo(null);

        setContextMenu({ x: point.x, y: point.y, district, shape: shapeDistricts(clickedFeature) });
      }
    }
  }, []);

  /**
   * The context menu's beacon item: with a beacon active (Beacons mode), add the shape's districts
   * to it, or remove them if any is in, as a click on an open beacon decides.
   */
  const beaconActionFor = (shape: { id: string; name: string }[]) => {
    if (!isEditingBeacon || shape.length === 0) return undefined;
    const verb: BeaconIdsVerb = shape.some((d) => highlightedSet.has(d.id)) ? 'Removed' : 'Added';
    const names: Record<string, string> = {};
    shape.forEach((d) => {
      if (d.name) names[d.id] = d.name;
    });
    return {
      verb,
      label: verb === 'Added' ? 'Add to beacon' : 'Remove from beacon',
      onClick: () => {
        setContextMenu(null);
        paintBeacon(shape.map((d) => d.id), verb, names);
      },
    };
  };

  const handleContextMenuOpenDistrict = useCallback(() => {
    if (contextMenu?.district) {
      const map = mapRef.current;

      // Remove click from previous feature
      if (clickedFeatureRef.current && map) {
        map.setFeatureState(
          {
            source: clickedFeatureRef.current.source,
            sourceLayer: clickedFeatureRef.current.sourceLayer,
            id: clickedFeatureRef.current.id
          },
          { clicked: false }
        );
      }

      // Set clicked state (we don't have GEOID in context menu, but we can skip the visual state)
      setHoverInfo(null);
      setSelectedDistrict(contextMenu.district);
      setIsSlideoutOpen(true);
      setContextMenu(null);
    }
  }, [contextMenu]);

  // Highlight the highlighted beacons' districts: a `beacon` feature state on every loaded
  // district holding one of their institutions, cleared from those that no longer do.
  useEffect(() => {
    if (!mapRef.current || !isStyleLoaded) return;
    const map = mapRef.current.getMap();
    // Not skipped while tiles load: the loaded ones can be reconciled now, and a skip would leave
    // what a stroke lit meanwhile unaccounted for; the tile load that follows runs this again.
    if (!map) return;
    // A stroke under way has lit its districts ahead of the store; it reconciles when it ends.
    if (strokeRef.current) return;

    cacheLoadedDistricts(map);
    const wanted = new window.Set(highlightedInstitutionIds);
    // Each lit district with its color: its first highlighted institution's beacon's, or the
    // beacon in hand's (a district just painted has no beacon yet).
    const next = new window.Map<string, string>();
    if (wanted.size > 0) {
      geoidToInstitutionsCache.current.forEach((institutionIds, geoid) => {
        const lit = institutionIds.find((id) => wanted.has(id));
        if (lit) next.set(geoid, highlightFills[lit] ?? beaconFill);
      });
    }
    const target = (id: string) => ({ source: 'districts', sourceLayer: SOURCE_LAYER, id });
    beaconGeoids.current.forEach((geoid) => {
      if (!next.has(geoid)) map.setFeatureState(target(geoid), { beacon: false });
    });
    // Every lit district takes its color again: a beacon recolored changes them in place.
    next.forEach((beaconColor, geoid) => map.setFeatureState(target(geoid), { beacon: true, beaconColor }));
    beaconGeoids.current = new window.Set(next.keys());

    // The highlighted districts' extents, for the radius circles. Published only when a box
    // changed: this effect runs on every tile load, and a new extents array would rebuild every
    // circle and resend them all to the map's worker for no visible change. Read only while a
    // radius is drawn: the tile read is slow, and painting a beacon (radius 0) would pay it on
    // every stroke.
    let changed = false;
    beaconBounds.current.forEach((_bounds, geoid) => {
      if (!next.has(geoid) || highlightedRadiusMiles === 0) {
        beaconBounds.current.delete(geoid);
        changed = true;
      }
    });
    if (next.size > 0 && highlightedRadiusMiles > 0) {
      map.querySourceFeatures('districts', { sourceLayer: SOURCE_LAYER }).forEach((feature: any) => {
        const geoid = feature.properties?.GEOID;
        if (!geoid || !next.has(geoid)) return;
        const prev = beaconBounds.current.get(geoid);
        const grown = growBounds(feature.geometry, prev);
        if (!grown || (prev && grown.every((edge, i) => edge === prev[i]))) return;
        beaconBounds.current.set(geoid, grown);
        changed = true;
      });
    }
    if (changed) setBeaconExtents([...beaconBounds.current.values()].map(extentOf));
  }, [isStyleLoaded, highlightedInstitutionIds, highlightFills, beaconFill, highlightedRadiusMiles, tilesLoadedTrigger, cacheLoadedDistricts]);

  // Shade the districts the query counted people at: a `headcount` feature state of
  // the people at the institutions of each loaded district (a school account matches no
  // district's shape), cleared from those that no longer have one.
  useEffect(() => {
    if (!mapRef.current || !isStyleLoaded) return;
    const map = mapRef.current.getMap();
    if (!map || !map.isSourceLoaded('districts')) return;

    cacheLoadedDistricts(map);
    const next = new window.Map<string, number>();
    if (headcounts) {
      geoidToInstitutionsCache.current.forEach((institutionIds, geoid) => {
        const people = institutionIds.reduce((sum, id) => sum + (headcounts[id] ?? 0), 0);
        if (people > 0) next.set(geoid, people);
      });
    }
    const target = (id: string) => ({ source: 'districts', sourceLayer: SOURCE_LAYER, id });
    headcountGeoids.current.forEach((geoid) => {
      if (!next.has(geoid)) map.setFeatureState(target(geoid), { headcount: null });
    });
    next.forEach((people, geoid) => map.setFeatureState(target(geoid), { headcount: people }));
    headcountGeoids.current = new window.Set(next.keys());
  }, [isStyleLoaded, headcounts, tilesLoadedTrigger, cacheLoadedDistricts]);

  // The circles are drawn at the slider's radius as it is: no easing, as each redraw rebuilds every
  // circle and sends them all to the map's worker, which a per-frame tween did many times over.
  const radiusData = useMemo(
    () => radiusCircles(beaconExtents, highlightedRadiusMiles),
    [beaconExtents, highlightedRadiusMiles],
  );

  // Listen for tile loads to incrementally build cache and update feature states
  // This ensures we update features as new tiles load (e.g., on initial page load or when panning)
  useEffect(() => {
    if (!mapRef.current || !isStyleLoaded) return;

    const map = mapRef.current.getMap();
    if (!map) return;

    const handleSourceData = (e: any) => {
      // Only handle district source when new tiles finish loading
      if (e.sourceId !== 'districts' || !e.isSourceLoaded) return;

      // Increment trigger so the beacon and headcount effects pick up the new tiles
      setTilesLoadedTrigger(prev => prev + 1);
    };

    map.on('sourcedata', handleSourceData);

    return () => {
      map.off('sourcedata', handleSourceData);
    };
  }, [isStyleLoaded]);

  // Memoize parsed districts data to avoid JSON parsing on every render
  const hoveredDistricts = useMemo(() => {
    if (!hoverInfo?.feature?.properties?.districts) {
      return [];
    }
    try {
      const allDistricts = JSON.parse(hoverInfo.feature.properties.districts);
      return allDistricts.filter((d: any) => d.id !== null);
    } catch (e) {
      console.error('Error parsing districts:', e);
      return [];
    }
  }, [hoverInfo]);

  /** Takes the hover highlight off its district and the hover card down. */
  const clearHover = () => {
    const map = mapRef.current;
    if (map && hoveredFeatureRef.current) {
      map.setFeatureState(
        {
          source: hoveredFeatureRef.current.source,
          sourceLayer: hoveredFeatureRef.current.sourceLayer,
          id: hoveredFeatureRef.current.id
        },
        { hover: false }
      );
      hoveredFeatureRef.current = null;
    }
    setHoverInfo(null);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Map
        ref={mapRef}
        // Where the map was left (a list view unmounts it), else the mainland US fitted to the
        // map, clear of the panel and the switch as the query's focus is.
        initialViewState={mapViewState ?? {
          bounds: [[-124.8, 24.4], [-66.9, 49.4]],
          fitBoundsOptions: { padding: { top: 70, right: 390, bottom: 40, left: 40 } }
        }}
        onMoveEnd={(e) => setMapViewState({ longitude: e.viewState.longitude, latitude: e.viewState.latitude, zoom: e.viewState.zoom })}
        mapStyle={MAP_STYLE}
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        style={{ width: '100%', height: '100%' }}
        projection="mercator"
        minZoom={4}
        maxZoom={10}
        interactiveLayerIds={['district-fill']}
        // The tools claim the drag themselves; Mapbox's own Shift+drag (box zoom) is off meanwhile.
        boxZoom={!isEditingBeacon}
        cursor={isEditingBeacon ? (isSpaceDown ? 'grab' : beaconTool !== 'pointer' ? 'crosshair' : 'pointer') : undefined}
        onMouseMove={onHover}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onClick={onClick}
        onContextMenu={onContextMenu}
        // Leaving the map's canvas mid-stroke ends the stroke there (a marquee waits for the mouseup,
        // which the window hears). onMouseLeave below is a different event: the pointer leaving a
        // district onto water or another state, where a stroke must go on.
        onMouseOut={() => {
          if (strokeRef.current) finishGesture();
          // Off the canvas, onto the panels over it: no district is under the pointer any more,
          // which the map cannot tell (its hover events stop with its mouse moves).
          clearHover();
        }}
        onMouseLeave={clearHover}
        // Hide hover info when starting to pan
        onDragStart={clearHover}
        onLoad={() => setIsStyleLoaded(true)}
      >
        {/* Custom background layers: the background paints land and hides the base style under
            it; Mapbox Streets has no land source-layer to fill. */}
        <Layer
          id="custom-background"
          type="background"
          paint={{ "background-color": "#393848" }}
        />
        <Layer
          id="custom-water"
          type="fill"
          source="composite"
          source-layer="water"
          paint={{ "fill-color": "#1f1f29" }}
        />

        {/* District tileset source and layers. A source's layers are added a tick after the
            style loads, when the state lines and labels below are already on the map, so each
            goes in just under the state lines: in the order written, and under every label.
            Never a beforeId naming a layer in this source: it is not on the map yet when the
            one naming it is added, and Mapbox refuses that with an error. */}
        <Source
          id="districts"
          type="vector"
          url={TILESET_URL}
          promoteId={{ [SOURCE_LAYER]: 'GEOID' }}
        >
          {/* The districts the query counted people at, under the beacons' fill */}
          <Layer
            id="district-headcount-fill"
            type="fill"
            source-layer={SOURCE_LAYER}
            beforeId="state-boundaries"
            paint={{
              "fill-color": HEADCOUNT_FILL_COLOR,
              "fill-opacity": [
                "step",
                ["coalesce", ["feature-state", "headcount"], 0],
                0,
                ...HEADCOUNT_OPACITY_STEPS.flat()
              ] as any,
              "fill-opacity-transition": { duration: 0 }
            }}
          />
          {/* The highlighted beacons' districts, under the hover and click fill */}
          <Layer
            id="district-beacon-fill"
            type="fill"
            source-layer={SOURCE_LAYER}
            beforeId="state-boundaries"
            paint={{
              // Each district in its beacon's color (the highlight effect sets it with the state)
              "fill-color": ["coalesce", ["feature-state", "beaconColor"], BEACON_FILL_COLOR] as any,
              "fill-opacity": ["case", ["boolean", ["feature-state", "beacon"], false], BEACON_FILL_OPACITY, 0],
              "fill-color-transition": { duration: 0 },
              "fill-opacity-transition": { duration: 0 }
            }}
          />
          <Layer
            id="district-fill"
            type="fill"
            source-layer={SOURCE_LAYER}
            beforeId="state-boundaries"
            paint={{
              "fill-color": [
                "case",
                // Clicked district - white (takes precedence)
                ["boolean", ["feature-state", "clicked"], false],
                "#ffffff",
                // Hovered district - white; with a beacon open, the
                // beacon's color over a district a click would add, white over one it would remove
                ["boolean", ["feature-state", "hover"], false],
                isEditingBeacon
                  ? ["case", ["boolean", ["feature-state", "beacon"], false], "#ffffff", beaconFill]
                  : "#ffffff",
                "transparent"
              ],
              "fill-opacity": [
                "case",
                ["boolean", ["feature-state", "clicked"], false],
                CLICKED_FILL_OPACITY,
                ["boolean", ["feature-state", "hover"], false],
                HOVER_FILL_OPACITY,
                0
              ],
              "fill-color-transition": { duration: 0 },
              "fill-opacity-transition": { duration: 0 }
            }}
          />
          {/* Base district borders - always visible with subtle styling */}
          <Layer
            id="district-border-base"
            type="line"
            source-layer={SOURCE_LAYER}
            beforeId="state-boundaries"
            paint={{
              "line-color": '#1f1f29',  // Subtle default color
              "line-width": 0.5,
              "line-color-transition": { duration: 0 },
              "line-width-transition": { duration: 0 }
            }}
          />
        </Source>

        {/* The query's radius: a circle around each beacon district, reaching the radius beyond it */}
        <Source id="beacon-radius" type="geojson" data={radiusData}>
          <Layer
            id="beacon-radius-fill"
            type="fill"
            paint={{
              "fill-color": beaconFill,
              // Gone while a search's purple shows, so the two do not fight; the dashed line stays.
              "fill-opacity": headcounts ? 0 : 0.08
            }}
          />
          <Layer
            id="beacon-radius-line"
            type="line"
            paint={{
              "line-color": beaconFill,
              "line-width": 1.5,
              "line-dasharray": [2, 2]
            }}
          />
        </Source>

        {/* State boundaries on top */}
        <Layer
          id="state-boundaries"
          type="line"
          source="composite"
          source-layer="admin"
          // US state lines and the US national border only: iso_3166_1 is "US" on a state line
          // and both countries' codes ("CA-US") on an international one (EDU-100).
          filter={['all', ['<=', ['get', 'admin_level'], 1], ['in', 'US', ['get', 'iso_3166_1']]]}
          minzoom={0}
          maxzoom={22}
          paint={{
            "line-color": "#aeacbf",
            "line-width": 1
          }}
        />

        {/* District names, for orientation: the larger districts first where space is short, a
            "+n" on a shape several districts share, brighter for the highlighted beacons'. */}
        <Layer
          id="district-labels"
          type="symbol"
          source="districts"
          source-layer={SOURCE_LAYER}
          minzoom={DISTRICT_LABEL_MIN_ZOOM}
          layout={{
            "text-field": [
              "case",
              [">", ["coalesce", ["get", "districtCount"], 1], 1],
              ["concat", ["get", "NAME"], " +", ["to-string", ["-", ["get", "districtCount"], 1]]],
              ["get", "NAME"]
            ],
            "text-size": ["interpolate", ["linear"], ["zoom"], DISTRICT_LABEL_MIN_ZOOM, 10, 10, 13],
            "text-max-width": 8,
            "text-padding": 4,
            // A lower key is placed first: the higher a district's enrollment percentile, the lower
            // its key, so the big districts keep their names where the small ones lose theirs.
            "symbol-sort-key": ["-", 100, ["coalesce", ["get", "enrollmentPercentileCountry"], 0]],
          }}
          paint={{
            "text-color": ["case", ["boolean", ["feature-state", "beacon"], false], LABEL_HIGHLIGHT_COLOR, LABEL_COLOR],
            // As the fills: a painted district's name changes at once, not over Mapbox's 300ms.
            "text-color-transition": { duration: 0 },
            "text-halo-color": LABEL_HALO_COLOR,
            "text-halo-width": 1,
            "text-opacity": 0.9
          }}
        />

        {/* US state names while zoomed out, from Mapbox's place data as the cities below */}
        <Layer
          id="state-labels"
          type="symbol"
          source="composite"
          source-layer="place_label"
          filter={[
            "all",
            ["==", ["get", "class"], "state"],
            ["==", ["get", "iso_3166_1"], "US"],
          ]}
          maxzoom={STATE_LABEL_MAX_ZOOM}
          layout={{
            "text-field": ["get", "name"],
            "text-size": ["interpolate", ["linear"], ["zoom"], 4, 11, STATE_LABEL_MAX_ZOOM, 15],
            "text-transform": "uppercase",
            "text-letter-spacing": 0.15,
            "text-max-width": 6,
          }}
          paint={{
            "text-color": LABEL_COLOR,
            "text-opacity": 0.8
          }}
        />

        {/* Major cities layer - uses Mapbox's built-in place data */}
        <Layer
          id="major-cities"
          type="symbol"
          source="composite"
          source-layer="place_label"
          minzoom={CITY_LABEL_MIN_ZOOM}
          maxzoom={CITY_LABEL_MAX_ZOOM}
          filter={[
            "all",
            ["==", ["get", "type"], "city"],
            ["<=", ["get", "symbolrank"], 10],  // Lower rank = larger cities (1-16 scale, 7 = ~top 100 cities)
            ["==", ["get", "iso_3166_1"], "US"],  // No Canadian or Mexican cities (EDU-100)
          ]}
          layout={{
            "text-field": ["get", "name"],
            "text-size": 12,
            "text-anchor": "center",
            "text-offset": [0, 0],
          }}
          paint={{
            "text-color": "#bebccc",
            "text-halo-color": "transparent",
            "text-halo-width": 1.5,
            "text-opacity": 0.8
          }}
        />
      </Map>

      {/* The rectangle or oval being dragged over the districts */}
      {marquee && (
        <div
          style={{
            position: 'absolute',
            pointerEvents: 'none',
            left: Math.min(marquee.x0, marquee.x1),
            top: Math.min(marquee.y0, marquee.y1),
            width: Math.abs(marquee.x1 - marquee.x0),
            height: Math.abs(marquee.y1 - marquee.y0),
            border: `1.5px dashed ${beaconFill}`,
            borderRadius: marquee.shape === 'oval' ? '50%' : 2,
            backgroundColor: marquee.verb === 'Added' ? `color-mix(in srgb, ${beaconFill} 15%, transparent)` : 'rgba(255, 255, 255, 0.15)',
            zIndex: 5,
          }}
        />
      )}

      {/* Tooltip on hover */}
      {hoverInfo && hoveredDistricts.length > 0 && (
        <div
          ref={tooltipRef}
          className="hoverTooltip"
          style={{
            left: hoverInfo.x + 10,
            top: hoverInfo.y + 10,
            overflow: 'hidden'
          }}
        >
          {hoveredDistricts.map((district: any, index: number) => (
            <DistrictHoverInfo
              key={index}
              district={district}
              isLast={index === hoveredDistricts.length - 1}
              matchingPeople={headcounts ? (headcounts[district.id] ?? 0) : undefined}
              editHint={isEditingBeacon ? (highlightedSet.has(district.id) ? 'remove' : 'add') : undefined}
            />
          ))}
        </div>
      )}

      {/* Context menu on right-click */}
      {contextMenu && (
        <DistrictContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          district={contextMenu.district}
          onClose={() => setContextMenu(null)}
          onOpenDistrict={handleContextMenuOpenDistrict}
          beaconAction={beaconActionFor(contextMenu.shape)}
        />
      )}

      {/* District slideout panel */}
      <ReactModal
        isOpen={isSlideoutOpen}
        shouldCloseOnEsc={true}
        shouldCloseOnOverlayClick={true}
        onRequestClose={() => {
          const map = mapRef.current;
          if (map && clickedFeatureRef.current) {
            map.setFeatureState(
              {
                source: clickedFeatureRef.current.source,
                sourceLayer: clickedFeatureRef.current.sourceLayer,
                id: clickedFeatureRef.current.id
              },
              { clicked: false }
            );
            clickedFeatureRef.current = null;
          }
          setIsSlideoutOpen(false);
        }}
        style={General.getSliderDefaults({ width: '1000px' })}
        closeTimeoutMS={200}
        parentSelector={General.getModalRoot}
      >
        {selectedDistrict && (
          <div>
            <InstitutionTopCard institution={selectedDistrict} roundedBottomCorners={false} />

            <div style={{ padding: '0 40px', marginTop: 0 }}>
              <div className="navigation-tabs" style={{ marginBottom: 20, marginTop: 20 }}>
                <div
                  className={`link ${activeTab === 'schools' ? 'active' : ''}`}
                  onClick={() => setActiveTab('schools')}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  Schools
                </div>
                <div
                  className={`link ${activeTab === 'people' ? 'active' : ''}`}
                  onClick={() => setActiveTab('people')}
                  style={{ cursor: 'pointer', display: 'inline-block' }}
                >
                  People
                </div>
              </div>
            </div>

            <div style={{ padding: '0 40px 40px 40px' }}>
              {activeTab === 'schools' ? (
                <>
                  {isLoadingSchools ? (
                    <LoadingBars />
                  ) : (
                    <>
                      <CustomList
                        searchResponse={schoolsSearchResponse}
                        isLoading={isLoadingSchools}
                        isExpanded={true}
                        onExpandToggle={() => {}}
                        isExporting={false}
                        isAllSortingDisabled={true}
                        showExpandButton={false}
                        showExportButton={false}
                        showSubheader={true}
                        showColumnVisibilityButton={false}
                        onSort={() => {}}
                        currentSort={{ sortBy: 'nr_students', sortDir: 'desc' }}
                        onExport={() => {}}
                        tableFields={globalContext.environment.entities.institution.tableFields}
                        entity={Entity.SCHOOL}
                      />
                      {schoolsSearchResponse && (
                        <TokenPagination
                          page={schoolsPager.page}
                          pageSize={25}
                          shown={schoolsSearchResponse.data.data.length}
                          total={schoolsSearchResponse.data.total}
                          hasNext={schoolsPager.hasNext}
                          isLoading={schoolsPager.loading}
                          onPrevious={schoolsPager.prev}
                          onNext={schoolsPager.next}
                        />
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  {isLoadingContacts ? (
                    <LoadingBars />
                  ) : (
                    <>
                      <CustomList
                        searchResponse={contactsSearchResponse}
                        isLoading={isLoadingContacts}
                        isExpanded={true}
                        onExpandToggle={() => {}}
                        isExporting={false}
                        isAllSortingDisabled={true}
                        showExpandButton={false}
                        showRowActionButtons={false}
                        showMainInstitution={false}
                        showExportButton={false}
                        showSubheader={true}
                        showColumnVisibilityButton={false}
                        onSort={() => {}}
                        currentSort={{ sortBy: 'priority', sortDir: 'desc' }}
                        onExport={() => {}}
                        tableFields={globalContext.environment.entities.contact.tableFields}
                        entity={Entity.CONTACT}
                      />
                      {contactsSearchResponse && (
                        <TokenPagination
                          page={contactsPager.page}
                          pageSize={25}
                          shown={contactsSearchResponse.data.data.length}
                          total={contactsSearchResponse.data.total}
                          hasNext={contactsPager.hasNext}
                          isLoading={contactsPager.loading}
                          onPrevious={contactsPager.prev}
                          onNext={contactsPager.next}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        <Tooltip id="main-tooltip" />
      </ReactModal>
    </div>
  );
}
